import { useMemo, useState, useCallback } from 'react';

/** Module multi-select state with toggle/clear/derived counts. */
export function useModuleSelection(modules, visibleIds) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected =
    visibleIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;

  const toggleOne = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }, [allVisibleSelected, visibleIds]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const selectedModules = useMemo(
    () => modules.filter((m) => selectedIds.has(m.module_id)),
    [modules, selectedIds],
  );

  const bulkImpact = useMemo(
    () => selectedModules.reduce(
      (acc, m) => ({
        sections: acc.sections + (m.total_sections || 0),
        slides: acc.slides + (m.total_slides || 0),
        quizzes: acc.quizzes + (m.total_quizzes || 0),
        enrollments: acc.enrollments + (m.enrolled_count || 0),
      }),
      { sections: 0, slides: 0, quizzes: 0, enrollments: 0 },
    ),
    [selectedModules],
  );

  return {
    selectedIds,
    setSelectedIds,
    selectedModules,
    selectedCount: selectedModules.length,
    bulkImpact,
    allVisibleSelected,
    someVisibleSelected,
    toggleOne,
    toggleAllVisible,
    clearSelection,
  };
}
